import { ImageFormat, processImageUrl } from "@/utils/process-image-url"
import { Image } from "./Image"
import { CrossOriginImage } from "./CrossOriginImage"
import clsx from "clsx"
import appIconPngUrl from "@assets/app-icon.png?url"
import { StyleResponseDTO } from "@/api/dto/styles"
import { Button } from "./Button"
import { projectAtom } from "@/store/project/projects-state"
import { useAtomValue } from "jotai"
import { useUserQuery } from "@/api/users-api"
import { useTranslation } from "react-i18next"
import { UserProfile } from "./UserProfile"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { showInternalStylesAtom } from "@/store/settings-state"
import { getLocalizedStyleDisplayName } from "@/i18n/style-display-name"

export const StyleCard = (props: {
	style: StyleResponseDTO
	isSelected?: boolean
	onSelect?: (style: StyleResponseDTO, target?: EventTarget, isFinished?: boolean) => void
	onViewClicked?: (style: StyleResponseDTO) => void
}) => {
	const { t } = useTranslation("components")
	const showInternalStyles = useAtomValue(showInternalStylesAtom)
	const userQuery = useUserQuery()
	const user = userQuery.data
	const style = props.style
	const currentProject = useAtomValue(projectAtom)
	const hasViewButton = Boolean(props.onViewClicked)

	const handleClick = (e: React.MouseEvent) => {
		if (props.onSelect) {
			props.onSelect(style, e.target)
		}
	}

	const handleViewClick = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (props.onViewClicked) {
			props.onViewClicked(style)
		}
	}

	const renderPreview = (index: number) => {
		return (
			<div
				key={index}
				className="flex aspect-[4/3] items-center justify-center overflow-hidden bg-base-700/50"
			>
				{style.coverImageUrl && (
					<Image
						aria-label={t("styleCard.ariaAiStylePreviewImage")}
						animateWhileLoading
						className="aspect-[4/3] object-cover"
						key={index}
						src={processImageUrl({
							url: style.coverImageUrl,
							format: ImageFormat.AVIF,
							shortestSidePxSizeOfResize: 600,
						})}
					/>
				)}
			</div>
		)
	}

	const renderStyleName = () => {
		const displayName = getLocalizedStyleDisplayName(style.name, t)
		return (
			<div className="flex items-center truncate py-0.5">
				<div className="truncate text-sm font-semibold">{displayName}</div>
			</div>
		)
	}

	const renderInternalBadge = () => {
		if (!showInternalStyles || style.visibility !== "internal") return null

		return (
			<span className="pointer-events-none absolute left-2 top-2 rounded bg-black px-1.5 py-0.5 text-xs font-semibold text-white shadow-sm">
				{t("styleCard.internal")}
			</span>
		)
	}

	const renderAuthorProfile = () => {
		if (style.author.profilePictureUrl) {
			return (
				<CrossOriginImage
					className="size-10 rounded-full"
					src={style.author.profilePictureUrl}
				/>
			)
		} else {
			if (style.author.name === "Pixieset") {
				return <img aria-hidden className="size-10 rounded-full" src={appIconPngUrl} />
			} else if (user) {
				return <UserProfile size={"small"} user={user} />
			}
		}
	}

	const renderAuthorName = () => {
		const displayName =
			style.author.name === "Pixieset" && !isPixiesetAuthEnabled()
				? "Polarr"
				: style.author.name

		return (
			<div className="overflow-hidden truncate text-xs font-medium text-base-400">
				{t("styleCard.byAuthor", { author: displayName })}
			</div>
		)
	}

	const renderSelectionOutline = (className: string) => {
		if (!props.isSelected) return null

		return (
			<div
				className={clsx("pointer-events-none absolute inset-0 z-10 rounded-lg", className)}
			/>
		)
	}

	return (
		<div
			role="group"
			aria-label={t("styleCard.ariaAiStyle", { name: style.name })}
			className={clsx(
				"relative flex w-full flex-1 flex-col truncate rounded-lg bg-base-700 hover:bg-base-600",
				!hasViewButton && "space-y-4",
			)}
			onClick={handleClick}
		>
			<div
				role="option"
				aria-label={t("styleCard.ariaSelectAiStyle", { name: style.name })}
				aria-selected={props.isSelected}
				className="relative overflow-hidden rounded-t-lg bg-base-800"
			>
				{renderInternalBadge()}
				{renderPreview(0)}
			</div>

			<div
				className={clsx(
					"flex flex-col",
					hasViewButton ? "justify-between space-y-4 p-4" : "space-y-2 px-4 pb-4",
				)}
			>
				<div className="flex flex-row items-center gap-3">
					<div className="pt-1">{renderAuthorProfile()}</div>
					<div className="flex min-w-0 flex-1 flex-col space-y-2 text-xs font-medium">
						{renderStyleName()}
						{renderAuthorName()}
					</div>
				</div>

				{hasViewButton && (
					<Button
						trackingLabel="style_view"
						aria-label={"View " + style.name + " AI Style Details"}
						trackingValues={{
							styleName: style.name,
							projectId: currentProject?.id ?? null,
						}}
						className="w-full bg-base-500 text-base-800 dark:text-base-200"
						onClick={handleViewClick}
					>
						{t("styleCard.view")}
					</Button>
				)}
			</div>
			{renderSelectionOutline("border-2 border-accent-600")}
		</div>
	)
}
